import gql from 'graphql-tag';
import { clients } from '../../../util/clients';
import {
  fieldsToGqlString,
  mergeByIdNewAtTop,
} from '../../../util/shared/vue-global';
import { sanitizeCCPaymentItem } from '../../../util/shared/ccSpecialPaymentType';

const { backend } = clients.direct;

const defaultTaxCCPaymentFields = {
  _id: '',
  paymentDate: '',
  systemDate: '',
  paidClearedDate: '',
  paymentNo: '', // not importing yet, have a look
  paymentGateway: '',
  merchantId: '',
  lengthOfTaxDetails: '',
  maskedAccount: '',
  transactionId: '',
  cardName: '',
  amountPaid: '', // paymentAmount === amountPaid
  processingFee: '',
  status: '',
  paymentType: '',
  specialPaymentType: '',
  modeOfPayment: '',
  apiLoginKey: '',
  timeChanged: '', // not importing yet, have a look
  paidBy: '',
  emailAddress: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  county: '', // not importing yet, have a look
  phoneNo: '',
  chunkID: '',
  isVoid: '',
  taxDetails: {
    paymentInstallment: '',
    taxYear: '',
    taxId: '',
    detailsCount: '',
    taxAmount: '',
    penalty: '',
    feeTotal: '',
    total: '',
  },
};

const defaultTaxWebPaymentConfigFields = {
  allowFullPayment: false,
  allowHalfPayment: false,
  allowCcpmt: false,
  pmtDelinq: false,
  disablepmtOption: false,
  disablepmtFromyear: 0,
  disablepmtToyear: 0,
  disableMtgPayments: false,
  mtgCodeList: '',
};

export const ccPayments = {
  namespaced: true,
  state: () => ({
    selectedItems: [],
    nextPaymentNumber: undefined,
    paymentNoRegPolling: undefined, // setInterval / clearInterval timer
    paymentSuccessFlag: false,
    taxWebPaymentConfig: { ...defaultTaxWebPaymentConfigFields },
  }),
  mutations: {
    setSelectedItems(state, items) {
      state.selectedItems = items;
    },
    setStats(state, results) {
      if (results.taxStatsCCMaxPaymentNo && results.taxStatsCCMaxPaymentNo.max !== undefined) {
        const nextPaymentNumber = results.taxStatsCCMaxPaymentNo.max + 1;
        if (state.nextPaymentNumber !== nextPaymentNumber) {
          state.nextPaymentNumber = nextPaymentNumber;
        }
      }
    },
    setPaymentRegPolling(state, timer) {
      if (state.paymentNoRegPolling) {
        clearInterval(state.paymentNoRegPolling);
      }
      state.paymentNoRegPolling = timer;
    },
    setPaymentsSuccessFlag(state, value) {
      state.paymentSuccessFlag = value;
    },
    setTaxWebPaymentConfig(state, config) {
      state.taxWebPaymentConfig = { ...defaultTaxWebPaymentConfigFields, ...config };
    },
  },
  actions: {
    async loadCCPaymentsTrans({ commit, dispatch }, {
      criteria, fields, baseCrudKey, postProccess,
    }) {
      let results;
      const gqlfields = fields || defaultTaxCCPaymentFields;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql` query taxCCPayments($criteria: ccPaymentsCriteriaType) {
            taxCCPayments(criteria: $criteria) {
                ${fieldsToGqlString(gqlfields)},
              }
            }`,
          variables: { criteria },
          fetchPolicy: 'no-cache',

        });
        clients.handleGQLErrors(results, commit);
        const ccPaymentTrans = results.data ? (results.data.taxCCPayments || []).map((item) => ({
          ...item,
          paidBy: (item.paidBy || '').toUpperCase(),
        })) : [];
        const postProccessData = postProccess ? postProccess(ccPaymentTrans) : ccPaymentTrans;
        commit('base/crud/setItems', [baseCrudKey, postProccessData], { root: true });
        return ccPaymentTrans;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was problem loading the Transaction List!', { root: true });
      }
      return [];
    },
    async upsertCCPaymentsTrans({ dispatch, rootGetters, commit }, {
      item, fields, baseCrudKey,
    }) {
      let results;
      try {
        const gqlfields = fields || defaultTaxCCPaymentFields;
        // Chokepoint: every CC save path (keyed, charged Forte/Payrix, the CC edit/bulk
        // screen) funnels through here. specialPaymentType is a strict GraphQL enum -- an
        // invalid value 500s at coercion before the resolver runs. Normalize, or omit it.
        const safeItem = sanitizeCCPaymentItem(item);
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertTaxCCPayment ($item: ccPaymentsInputType!)  {
                upsertTaxCCPayment(upsert: $item) {
                    ${fieldsToGqlString(gqlfields)},
                  }
                }`,
          variables: {
            item: safeItem,
          },
        });
        clients.handleGQLErrors(results, commit);
        const ccPaymentsTransStateData = rootGetters['base/crud/items'](baseCrudKey);
        const newTransaction = results.data ? (results.data.upsertTaxCCPayment
          || {}) : {};
        const newList = mergeByIdNewAtTop(ccPaymentsTransStateData || [], newTransaction);
        commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
        return newTransaction;
      } catch (error) {
        dispatch('logException', error, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem saving the credit card payment transaction!', { root: true });
        // Re-throw error so calling code knows the CC payment upsert failed
        throw error;
      }
    },
    async delCCPaymentsTrans({ commit, rootGetters, dispatch }, { item, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation deleteTaxCCPayment ($id: ID!)  {
            deleteTaxCCPayment(id: $id) {
                  _id
                }
              }`,
          variables: {
            id: item._id,
          },
        });
        clients.handleGQLErrors(results, commit);
        const delId = results.data.deleteTaxCCPayment._id;
        const transData = rootGetters['base/crud/items'](baseCrudKey);
        const updatedData = transData.filter((d) => d._id !== delId);
        commit('base/crud/setItems', [baseCrudKey, updatedData], { root: true });
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in deleting the transaction/s.', { root: true });
      }
    },
    async loadLastPaymentNo({ commit, dispatch }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`query taxStatsCCMaxPaymentNo {
            taxStatsCCMaxPaymentNo { max }
          }`,
          variables: {},
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        commit('setStats', results.data);
        return results.data.taxStatsCCMaxPaymentNo.max;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem determining the last payment number.', { root: true });
      }
      return {};
    },
    async loadLatestCCDate({ commit, dispatch }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`query taxStatsCCLatestCCDate {
            taxStatsCCLatestCCDate { latestCCDate }
          }`,
          variables: {},
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        commit('setStats', results.data);
        return results.data.taxStatsCCLatestCCDate.latestCCDate;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem determining the latest CC Date.', { root: true });
      }
      return {};
    },
    async loadTaxWebPaymentConfig({ commit, dispatch }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`query taxWebPaymentConfig {
            taxWebPaymentConfig {
              allowFullPayment
              allowHalfPayment
              allowCcpmt
              pmtDelinq
              disablepmtOption
              disablepmtFromyear
              disablepmtToyear
              disableMtgPayments
              mtgCodeList
            }
          }`,
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        const config = results.data ? (results.data.taxWebPaymentConfig || {}) : {};
        commit('setTaxWebPaymentConfig', config);
        return config;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem loading the payment configuration.', { root: true });
      }
      return {};
    },
    async upsertTaxWebPaymentConfig({ commit, dispatch }, { upsert }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertTaxWebPaymentConfig($upsert: taxWebPaymentConfigInputType) {
            upsertTaxWebPaymentConfig(upsert: $upsert) {
              allowFullPayment
              allowHalfPayment
              allowCcpmt
              pmtDelinq
              disablepmtOption
              disablepmtFromyear
              disablepmtToyear
              disableMtgPayments
              mtgCodeList
            }
          }`,
          variables: { upsert },
        });
        clients.handleGQLErrors(results, commit);
        return results.data ? (results.data.upsertTaxWebPaymentConfig || {}) : {};
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem saving the payment configuration.', { root: true });
      }
      return {};
    },
    async updateGlobalTaxWebsiteSystemConfig({ commit, dispatch }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql` query updateGlobalTaxWebsiteSystemConfig($criteria: ccPaymentsTaxWebSiteInputType) {
            updateGlobalTaxWebsiteSystemConfig(criteria: $criteria) {
              success,
              message
            }
          }`,
          variables: {
            criteria: {},
          },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, dispatch);
        dispatch('flashError', 'There was a problem updating the Tax Website System Config.', { root: true });
      }
    },
  },
};

export default ccPayments;
